import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { layoutStore, type PageLayoutStore } from 'shared/layout'
import { uiStore } from 'shared/store/uiStore'
import type {
  IResponseCallHistoryItem,
  IParamsGetCallHistoryStatistic,
} from 'entities/CallHistory/api/types'
import { type CallHistory } from 'entities/CallHistory/model/CallHistory'
import type { IResponseEventInboxCalling } from 'entities/Inbox/api/types'
import { ConversationRoutes } from 'entities/ExportRouters'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { CallModalStore } from 'widgets/CallModal'
import type { ContactsDetailsManageStore } from 'widgets/ContactsDetails/store'
import type { IOpenConversationDetails } from 'widgets/ContactsDetails'
import { FiltersStore } from 'pages/conversations/pages/calls/ui/CallHistoryDropdownFilter'
import { CallHistoryFiltersStore } from 'pages/conversations/pages/calls/ui/CallHistoryFilters'
import { CallHistoryTableStore } from 'pages/conversations/pages/calls/ui/CallHistoryTable'

export class CallHistoryStore {
  readonly callHistoryFiltersStore: CallHistoryFiltersStore
  readonly callHistoryTableStore: CallHistoryTableStore
  readonly filtersStore: FiltersStore

  // === Reactions ==== ///
  private _disposeReactionEventFilters: IReactionDisposer | null = null

  constructor(
    private _callModalStore: CallModalStore,
    private _contactsDetailsManageStore: ContactsDetailsManageStore,
    private _pageLayoutStore: PageLayoutStore,
    private _entities: IEntitiesStore
  ) {
    this.callHistoryFiltersStore = new CallHistoryFiltersStore()
    this.callHistoryTableStore = new CallHistoryTableStore(
      this._entities,
      this.callHistoryFiltersStore
    )
    this.filtersStore = new FiltersStore(this._entities, {
      onChangeFilter: (key) => {
        this.callHistoryTableStore.setGroup(key)
      },
    })

    makeAutoObservable(this)
  }

  initReactions = () => {
    this.callHistoryTableStore.initReactions()
    this.reactionEventFilters()
  }

  disposeReactions = () => {
    this.callHistoryTableStore.disposeReactions()
    this._disposeReactionEventFilters?.()
  }

  reactionEventFilters = () => {
    this._disposeReactionEventFilters?.()
    this._disposeReactionEventFilters = reaction(() => this.eventFilters, this.sendLog)
  }

  get onOpenContactsDetails(): IOpenConversationDetails {
    return {
      disabled: this._contactsDetailsManageStore.disabled,
      open: (data) => {
        this._contactsDetailsManageStore.onOpenConversationDetails(data, 'call history')
      },
    }
  }

  get callModalStore() {
    return this._callModalStore
  }

  get isLoading() {
    return this.callHistoryTableStore.isLoading
  }

  get isLoadingStatistics() {
    return this.callHistoryFiltersStore.loading
  }

  get isEmptySearchResults() {
    return this.callHistoryTableStore.isEmptySearchResults && !this.isLoadingStatistics
  }

  get isEmptyState() {
    return (
      !this.isLoadingStatistics &&
      !Boolean(this.callHistoryFiltersStore.filtersList.length) &&
      this.callHistoryTableStore.isEmptyState
    )
  }

  get filtersList() {
    return this.callHistoryFiltersStore.filtersList
  }

  get group() {
    return this.filtersStore.filter?.key
  }

  get groupLabel() {
    return this.filtersStore.filter?.label
  }

  get eventFilters() {
    return {
      outcome: this.callHistoryFiltersStore.activeFilter?.outcome,
      source: this.callHistoryFiltersStore.activeFilter?.source,
      conversation_type: this.group,
    }
  }

  updateInboxCalling = (data: IResponseEventInboxCalling) => {
    this.callHistoryTableStore.updateInboxCalling(data)
  }

  markAsReadByConversation = (conversationId: number, inboxId: number) => {
    this.callHistoryTableStore.markAsReadByConversation(conversationId, inboxId)
  }

  syncFilters = () => {
    const params: IParamsGetCallHistoryStatistic = {
      team_id: this._entities.inboxesStore.currentInboxTypeId,
      team_type: this._entities.inboxesStore.currentInboxType || 3,
      group: this.filtersStore.filter?.key === 'all' ? null : this.filtersStore.filter?.key,
    }

    this.callHistoryFiltersStore.syncFilters(params)
  }

  updateCallHistoryByMessageReceivedEvent = (data: IResponseCallHistoryItem) => {
    this.callHistoryTableStore.updateCallHistoryByMessageReceivedEvent(data)
  }

  updateCallHistoryByVoicemailEvent = (data: IResponseCallHistoryItem) => {
    this.callHistoryTableStore.updateCallHistoryByVoicemailEvent(data)
  }

  sendLog = () => {
    const activeFilter = this.callHistoryFiltersStore.activeFilter
    const outcome = this.callHistoryFiltersStore.isActiveAllCalls
      ? 'all-calls'
      : activeFilter?.source
      ? `${activeFilter.type} - ${activeFilter.source}`
      : `${activeFilter?.type} - ${activeFilter?.outcome}`

    const payload = {
      event_id: 'call_history_filter_used',
      outcome,
      conversation_type: this.groupLabel,
    }

    this._entities.eventLogStore.logEvent('Call History Filter Used', payload, {
      groupId: this._entities.organizationStore.id,
    })
  }

  dispose = () => {
    this.callHistoryTableStore.clear()
    this.callHistoryTableStore.dispose()
    this.callHistoryFiltersStore.reset()
    this.filtersStore.reset()
  }

  onOpenConversation = (callHistory: CallHistory) => {
    if (layoutStore.isMobileView) {
      uiStore.changeRoute({
        path: `/${ConversationRoutes.root}/${ConversationRoutes.calls}/${callHistory.conversation.id}`,
      })
      this._pageLayoutStore.toEndContainer()
    } else {
      this.onOpenContactsDetails.open({
        contactId: callHistory.contact?.id,
        inboxId: callHistory.inbox.id,
        conversationId: callHistory.conversation.id,
      })
    }
  }
}
